import React, { useState } from "react";
import {
  Button,
  ContactDetails,
  ContactFormWrapper,
  ContactParagraph,
  ContactQuestionTitle,
  ContactTitleFirst,
  ContactWrapperList,
  ContactsWrapper,
  ErrorMessage,
  FormContainer,
  FormField,
  FormFieldMessage,
  Input,
  Label,
  RowWrapper,
  SubmitErrorMessage,
  SuccessMessage,
  TelephoneWrapper,
  TextArea,
  TextField,
} from "./contact.styles";
import { FaLocationDot } from "react-icons/fa6";
import { MdCall } from "react-icons/md";
import { MdOutlineMail } from "react-icons/md";
import { IoPersonCircleOutline } from "react-icons/io5";
import { CiCircleInfo } from "react-icons/ci";
import { FaPencil } from "react-icons/fa6";
import { FiSend } from "react-icons/fi";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
    honeypot: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const sanitizedValue = value.replace(/[^\d+\s]/g, "");
      setFormData({ ...formData, [name]: sanitizedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.firstName.trim()) formErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) formErrors.lastName = "Last name is required";

    const normalizedPhone = formData.phone.replace(/\s+/g, "");
    if (!normalizedPhone) {
      formErrors.phone = "Phone number is required";
    } else {
      const ukPhoneRegex = /^(?:0\d{10}|\+44\d{10})$/;
      if (!ukPhoneRegex.test(normalizedPhone)) {
        formErrors.phone = "Invalid UK phone number";
      }
    }

    if (!formData.email.trim()) {
      formErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email.trim())) {
      formErrors.email = "Invalid email address";
    }

    if (!formData.subject.trim()) formErrors.subject = "Subject is required";
    if (!formData.message.trim()) formErrors.message = "Message is required";

    if (formData.honeypot) formErrors.honeypot = "Spam detected"; // Honeypot trap

    setErrors(formErrors);

    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const response = await fetch("https://formspree.io/f/xovagkgj", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            phone: formData.phone.trim(),
            email: formData.email.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim(),
            _gotcha: formData.honeypot,
          }),
        });

        if (response.ok) {
          setSubmitted(true);
          setFormData({
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            subject: "",
            message: "",
            honeypot: "",
          });
          setErrors({});
        } else {
          setSubmitError("We could not send your message right now. Please try again shortly.");
        }
      } catch (error) {
        setSubmitError("Network error while sending your message. Please check your connection and try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <ContactFormWrapper>
        <ContactDetails>
          <ContactTitleFirst>Contact us</ContactTitleFirst>
          <ContactQuestionTitle>Have questions? Get in touch.</ContactQuestionTitle>
          <ContactParagraph>Whether you need assistance, have enquiries about our machinery, or want to discuss the right solution for your site, our team is ready to help.</ContactParagraph>
          <ContactsWrapper>
            <ContactWrapperList>
              <FaLocationDot />
              Highbridge Estates, Brambridge, Eastleigh, SO50 6HS
            </ContactWrapperList>
            <ContactWrapperList>
              <MdCall /> <TelephoneWrapper href="tel:01962717627">01962 717627</TelephoneWrapper>
            </ContactWrapperList>
            <ContactWrapperList>
              <MdOutlineMail />
              <a href="mailto:Keith@inertrecycling.co.uk">Keith@inertrecycling.co.uk</a>
            </ContactWrapperList>
          </ContactsWrapper>
        </ContactDetails>

        {!submitted ? (
          <FormContainer onSubmit={handleSubmit}>
            <RowWrapper>
              <TextField>
                <Label htmlFor="firstName">
                  <IoPersonCircleOutline />
                  First Name<span className="requiredField">*</span>
                </Label>
                <Input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleChange} required aria-invalid={Boolean(errors.firstName)} />
                {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
              </TextField>

              <FormField>
                <Label htmlFor="lastName">
                  <IoPersonCircleOutline />
                  Last Name<span className="requiredField">*</span>
                </Label>
                <Input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleChange} required aria-invalid={Boolean(errors.lastName)} />
                {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
              </FormField>
            </RowWrapper>

            <RowWrapper>
              <FormField>
                <Label htmlFor="email">
                  <MdOutlineMail />
                  Email<span className="requiredField">*</span>
                </Label>
                <Input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required aria-invalid={Boolean(errors.email)} />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormField>

              <FormField>
                <Label htmlFor="phone">
                  <MdCall />
                  Phone<span className="requiredField">*</span>
                </Label>
                <Input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} required aria-invalid={Boolean(errors.phone)} placeholder="e.g. 07123 456789 or +447123456789" />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </FormField>
            </RowWrapper>

            <FormFieldMessage>
              <Label htmlFor="subject">
                <CiCircleInfo />
                Subject<span className="requiredField">*</span>
              </Label>
              <Input id="subject" type="text" name="subject" value={formData.subject} onChange={handleChange} required aria-invalid={Boolean(errors.subject)} />
              {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
            </FormFieldMessage>

            <FormFieldMessage>
              <Label htmlFor="message">
                <FaPencil />
                Message<span className="requiredField">*</span>
              </Label>
              <TextArea id="message" name="message" value={formData.message} onChange={handleChange} required aria-invalid={Boolean(errors.message)} />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </FormFieldMessage>

            <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} style={{ display: "none" }} />

            {submitError && <SubmitErrorMessage>{submitError}</SubmitErrorMessage>}

            <Button type="submit" disabled={isSubmitting}>
              <FiSend />
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </FormContainer>
        ) : (
          <SuccessMessage>Thank you. Your message has been sent successfully and a member of our team will be in touch shortly.</SuccessMessage>
        )}
      </ContactFormWrapper>
    </>
  );
};

export default ContactUs;
